import {
  tChatEventError,
  tContent,
  tMessage,
} from 'src/components/Chat/Chat.types.ts';
import { eComponentType } from 'src/pages/WhatsappApi/WhatsappApi.enum.ts';
import { tComponent } from 'src/pages/WhatsappApi/WhatsappApi.types.ts';
import { eChannelType } from 'src/providers/SocketsProvider/Sockets.enums.ts';
import { withoutRetryAction } from 'src/utils/metaErrorNotice.ts';
import { tMetaErrorNotice } from 'src/utils/metaErrorNotice.types.ts';
import { resolveOutgoingMetaErrorNotice } from '../MessageBubble/MessageBubbleV2.functions.ts';

/**
 * Adapts a chat event's `content.carousel` (INT-1302) into the `tComponent[]`
 * shape the shared TemplateCarouselPreview already consumes on its six other
 * call sites, so the sent-template bubble reuses the proven render path
 * instead of growing a second one.
 *
 * Returns `undefined` — not `[]` — when there is nothing to draw, because the
 * preview's own null guard reads `getComponentByType(...)?.cards`, and an
 * empty array would still mount the wrapper element.
 *
 * The cards are passed through verbatim: no filtering by button `type` (a real
 * staging payload carries URL buttons alongside QUICK_REPLY ones and both must
 * render), no lowercasing of `type`/`format` (the enums are uppercase), no URL
 * rewriting (the stored header_handle URLs are already public and render as-is
 * in the chat's template picker).
 */
export function buildCarouselComponents(
  content: tContent | null | undefined,
): tComponent[] | undefined {
  const cards = content?.carousel?.cards;

  if (!cards?.length) {
    return undefined;
  }

  return [{ type: eComponentType.CAROUSEL, cards }];
}

/**
 * INT-1500: the Meta error notice of a template bubble, the same one a
 * message gets (INT-1503) minus "Reintentar": messaging retries only session
 * messages Meta never accepted, never a template, so that button would always
 * answer "no". Undefined while the template is not failed or held, and outside
 * WhatsApp API chats.
 */
export function resolveTemplateMetaErrorNotice(
  event: {
    message: Pick<tMessage, 'fromMe' | 'state' | 'error'> | null;
    errors?: tChatEventError[];
  },
  channel: eChannelType | string | undefined,
): tMetaErrorNotice | undefined {
  const notice = resolveOutgoingMetaErrorNotice(
    event.message ? { ...event.message, errors: event.errors } : null,
    channel,
  );
  return notice && withoutRetryAction(notice);
}
